import { useState, useContext } from 'react';
import AppContext from '../AppContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useModalClose } from '../hooks/useModalClose';

export default function TipAnalysisModal({ isOpen, onClose }) {
    const { theme } = useContext(AppContext);
    const { t } = useLanguage();
    useModalClose(onClose, isOpen);

    const [csvFile, setCsvFile] = useState(null);
    const [csvData, setCsvData] = useState([]);
    const [shiftStart, setShiftStart] = useState('12:00');
    const [shiftEnd, setShiftEnd] = useState('23:59');
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCsvFile(file);
            setError(null);
        }
    };

    const parseCSV = (text) => {
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length < 2) {
            throw new Error(t('invalidCsvFormat'));
        }

        const headers = lines[0].split(',').map(h => h.trim());
        const data = [];

        for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map(v => v.trim());
            const row = {};
            headers.forEach((header, index) => {
                row[header] = values[index];
            });
            data.push(row);
        }

        return data;
    };

    const analyzeTips = () => {
        if (!csvData || csvData.length === 0) {
            setError(t('error') + ': ' + t('noData'));
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Parse shift times
            const [startHour, startMin] = shiftStart.split(':').map(Number);
            const [endHour, endMin] = shiftEnd.split(':').map(Number);

            // Group transactions by date and shift
            const shifts = {};

            csvData.forEach(row => {
                // Parse date and time from CSV
                const fecha = row.fecha; // format: 2025-10-18
                const hora = row.hora;   // format: 22:33:50
                const propina = parseFloat(row.propina) || 0;

                if (!fecha || !hora) return;

                const [hour, min] = hora.split(':').map(Number);
                const timeInMinutes = hour * 60 + min;
                const startTimeInMinutes = startHour * 60 + startMin;
                const endTimeInMinutes = endHour * 60 + endMin;

                // Determine which shift this belongs to
                let shiftDate;
                let isInShift = false;

                if (startTimeInMinutes > endTimeInMinutes) {
                    // Shift crosses midnight (e.g., 12:00 to 11:59 next day)
                    if (timeInMinutes >= startTimeInMinutes) {
                        // Transaction is after shift start on the same day
                        shiftDate = fecha;
                        isInShift = true;
                    } else if (timeInMinutes <= endTimeInMinutes) {
                        // Transaction is before shift end (early morning of next day)
                        // This belongs to the previous day's shift
                        const date = new Date(fecha);
                        date.setDate(date.getDate() - 1);
                        shiftDate = date.toISOString().split('T')[0];
                        isInShift = true;
                    }
                } else {
                    // Shift is within the same day
                    if (timeInMinutes >= startTimeInMinutes && timeInMinutes <= endTimeInMinutes) {
                        shiftDate = fecha;
                        isInShift = true;
                    }
                }

                if (isInShift && shiftDate) {
                    if (!shifts[shiftDate]) {
                        shifts[shiftDate] = {
                            date: shiftDate,
                            totalTips: 0,
                            transactionCount: 0,
                            transactions: []
                        };
                    }

                    shifts[shiftDate].totalTips += propina;
                    shifts[shiftDate].transactionCount += 1;
                    shifts[shiftDate].transactions.push({
                        time: hora,
                        amount: propina,
                        total: parseFloat(row.total) || 0
                    });
                }
            });

            // Convert to array and sort by date
            const shiftsArray = Object.values(shifts).sort((a, b) =>
                new Date(b.date) - new Date(a.date)
            );

            // Calculate totals
            const totalTips = shiftsArray.reduce((sum, shift) => sum + shift.totalTips, 0);
            const totalTransactions = shiftsArray.reduce((sum, shift) => sum + shift.transactionCount, 0);

            setAnalysis({
                shifts: shiftsArray,
                totalTips,
                totalTransactions,
                averageTipPerShift: shiftsArray.length > 0 ? totalTips / shiftsArray.length : 0,
                averageTipPerTransaction: totalTransactions > 0 ? totalTips / totalTransactions : 0
            });

        } catch (err) {
            console.error('Error analyzing tips:', err);
            setError(t('error') + ': ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleImportCSV = async () => {
        if (!csvFile) {
            setError(t('error') + ': ' + t('pleaseSelectFile'));
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const text = await csvFile.text();
            const data = parseCSV(text);
            setCsvData(data);
            setError(null);
            alert(t('dataImported') + ': ' + data.length + ' ' + t('transactions'));
        } catch (err) {
            console.error('Error importing CSV:', err);
            setError(t('error') + ': ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(amount);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString + 'T00:00:00');
        return date.toLocaleDateString('es-MX', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (!isOpen) return null;

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '1rem'
            }}
            onClick={onClose}
        >
            <div
                style={{
                    backgroundColor: theme?.colors?.surface || '#fff',
                    borderRadius: theme?.borderRadius || 8,
                    padding: '1.5rem',
                    maxWidth: '900px',
                    width: '100%',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    color: theme?.colors?.text || '#000'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5rem',
                    borderBottom: `2px solid ${theme?.colors?.primary || '#1976d2'}`,
                    paddingBottom: '0.5rem'
                }}>
                    <h2 style={{
                        color: theme?.colors?.primary || '#1976d2',
                        margin: 0,
                        fontSize: '1.5rem'
                    }}>
                        {t('creditTips')} {t('analysis')}
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            backgroundColor: theme?.colors?.error || '#d32f2f',
                            color: theme?.colors?.surface || '#fff',
                            border: 'none',
                            borderRadius: theme?.borderRadius || 4,
                            padding: '0.5rem 1rem',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            fontWeight: 'bold'
                        }}
                    >
                        {t('close')}
                    </button>
                </div>

                {/* File Import Section */}
                <div style={{
                    backgroundColor: theme?.colors?.background || '#f5f5f5',
                    padding: '1rem',
                    borderRadius: theme?.borderRadius || 4,
                    marginBottom: '1rem'
                }}>
                    <h3 style={{
                        margin: '0 0 1rem 0',
                        color: theme?.colors?.text || '#000',
                        fontSize: '1.2rem'
                    }}>
                        1. {t('import')} CSV
                    </h3>

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.5rem',
                            fontWeight: 'bold',
                            color: theme?.colors?.text || '#000'
                        }}>
                            {t('selectFile')}:
                        </label>
                        <input
                            type="file"
                            accept=".csv"
                            onChange={handleFileChange}
                            style={{
                                padding: '0.5rem',
                                borderRadius: theme?.borderRadius || 4,
                                border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                backgroundColor: theme?.colors?.surface || '#fff',
                                color: theme?.colors?.text || '#000',
                                width: '100%'
                            }}
                        />
                    </div>

                    <button
                        onClick={handleImportCSV}
                        disabled={!csvFile || loading}
                        style={{
                            backgroundColor: !csvFile || loading ? '#ccc' : theme?.colors?.primary || '#1976d2',
                            color: theme?.colors?.surface || '#fff',
                            border: 'none',
                            borderRadius: theme?.borderRadius || 4,
                            padding: '0.75rem 1.5rem',
                            cursor: !csvFile || loading ? 'not-allowed' : 'pointer',
                            fontSize: '1rem',
                            fontWeight: 'bold'
                        }}
                    >
                        {loading ? t('importing') + '...' : t('import') + ' CSV'}
                    </button>

                    {csvData.length > 0 && (
                        <div style={{
                            marginTop: '0.5rem',
                            color: theme?.colors?.success || '#4caf50',
                            fontWeight: 'bold'
                        }}>
                            {csvData.length} {t('transactions')} {t('imported')}
                        </div>
                    )}
                </div>

                {/* Shift Time Selection */}
                {csvData.length > 0 && (
                    <div style={{
                        backgroundColor: theme?.colors?.background || '#f5f5f5',
                        padding: '1rem',
                        borderRadius: theme?.borderRadius || 4,
                        marginBottom: '1rem'
                    }}>
                        <h3 style={{
                            margin: '0 0 1rem 0',
                            color: theme?.colors?.text || '#000',
                            fontSize: '1.2rem'
                        }}>
                            2. {t('defineShiftTimes')}
                        </h3>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '1rem',
                            marginBottom: '1rem'
                        }}>
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: 'bold',
                                    color: theme?.colors?.text || '#000'
                                }}>
                                    {t('shiftStart')}:
                                </label>
                                <input
                                    type="time"
                                    value={shiftStart}
                                    onChange={(e) => setShiftStart(e.target.value)}
                                    style={{
                                        padding: '0.5rem',
                                        borderRadius: theme?.borderRadius || 4,
                                        border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                        backgroundColor: theme?.colors?.surface || '#fff',
                                        color: theme?.colors?.text || '#000',
                                        width: '100%',
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: 'bold',
                                    color: theme?.colors?.text || '#000'
                                }}>
                                    {t('shiftEnd')}:
                                </label>
                                <input
                                    type="time"
                                    value={shiftEnd}
                                    onChange={(e) => setShiftEnd(e.target.value)}
                                    style={{
                                        padding: '0.5rem',
                                        borderRadius: theme?.borderRadius || 4,
                                        border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                        backgroundColor: theme?.colors?.surface || '#fff',
                                        color: theme?.colors?.text || '#000',
                                        width: '100%',
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>
                        </div>

                        <button
                            onClick={analyzeTips}
                            disabled={loading}
                            style={{
                                backgroundColor: loading ? '#ccc' : theme?.colors?.success || '#4caf50',
                                color: theme?.colors?.surface || '#fff',
                                border: 'none',
                                borderRadius: theme?.borderRadius || 4,
                                padding: '0.75rem 1.5rem',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {loading ? t('analyzing') + '...' : t('analyze') + ' ' + t('tips')}
                        </button>
                    </div>
                )}

                {/* Error Display */}
                {error && (
                    <div style={{
                        backgroundColor: '#ffebee',
                        color: '#c62828',
                        padding: '1rem',
                        borderRadius: theme?.borderRadius || 4,
                        marginBottom: '1rem',
                        border: '1px solid #ef5350'
                    }}>
                        {error}
                    </div>
                )}

                {/* Analysis Results */}
                {analysis && (
                    <div style={{
                        backgroundColor: theme?.colors?.background || '#f5f5f5',
                        padding: '1rem',
                        borderRadius: theme?.borderRadius || 4
                    }}>
                        <h3 style={{
                            margin: '0 0 1rem 0',
                            color: theme?.colors?.text || '#000',
                            fontSize: '1.2rem'
                        }}>
                            3. {t('results')}
                        </h3>

                        {/* Summary Cards */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '1rem',
                            marginBottom: '1.5rem'
                        }}>
                            <div style={{
                                backgroundColor: theme?.colors?.surface || '#fff',
                                padding: '1rem',
                                borderRadius: theme?.borderRadius || 4,
                                boxShadow: theme?.boxShadow || '0 2px 4px rgba(0,0,0,0.1)',
                                textAlign: 'center'
                            }}>
                                <div style={{
                                    fontSize: '0.9rem',
                                    color: theme?.colors?.textSecondary || '#666',
                                    marginBottom: '0.5rem'
                                }}>
                                    {t('total')} {t('tips')}
                                </div>
                                <div style={{
                                    fontSize: '1.5rem',
                                    fontWeight: 'bold',
                                    color: theme?.colors?.success || '#4caf50'
                                }}>
                                    {formatCurrency(analysis.totalTips)}
                                </div>
                            </div>

                            <div style={{
                                backgroundColor: theme?.colors?.surface || '#fff',
                                padding: '1rem',
                                borderRadius: theme?.borderRadius || 4,
                                boxShadow: theme?.boxShadow || '0 2px 4px rgba(0,0,0,0.1)',
                                textAlign: 'center'
                            }}>
                                <div style={{
                                    fontSize: '0.9rem',
                                    color: theme?.colors?.textSecondary || '#666',
                                    marginBottom: '0.5rem'
                                }}>
                                    {t('average')} / {t('shift')}
                                </div>
                                <div style={{
                                    fontSize: '1.5rem',
                                    fontWeight: 'bold',
                                    color: theme?.colors?.primary || '#1976d2'
                                }}>
                                    {formatCurrency(analysis.averageTipPerShift)}
                                </div>
                            </div>

                            <div style={{
                                backgroundColor: theme?.colors?.surface || '#fff',
                                padding: '1rem',
                                borderRadius: theme?.borderRadius || 4,
                                boxShadow: theme?.boxShadow || '0 2px 4px rgba(0,0,0,0.1)',
                                textAlign: 'center'
                            }}>
                                <div style={{
                                    fontSize: '0.9rem',
                                    color: theme?.colors?.textSecondary || '#666',
                                    marginBottom: '0.5rem'
                                }}>
                                    {t('totalShifts')}
                                </div>
                                <div style={{
                                    fontSize: '1.5rem',
                                    fontWeight: 'bold',
                                    color: theme?.colors?.text || '#000'
                                }}>
                                    {analysis.shifts.length}
                                </div>
                            </div>
                        </div>

                        {/* Shifts Table */}
                        <div style={{
                            overflowX: 'auto'
                        }}>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                backgroundColor: theme?.colors?.surface || '#fff',
                                borderRadius: theme?.borderRadius || 4,
                                overflow: 'hidden'
                            }}>
                                <thead>
                                    <tr style={{
                                        backgroundColor: theme?.colors?.primary || '#1976d2',
                                        color: theme?.colors?.surface || '#fff'
                                    }}>
                                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>{t('date')}</th>
                                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>{t('transactions')}</th>
                                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>{t('total')} {t('tips')}</th>
                                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>{t('average')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {analysis.shifts.map((shift, index) => (
                                        <tr key={shift.date} style={{
                                            backgroundColor: index % 2 === 0
                                                ? theme?.colors?.surface || '#fff'
                                                : theme?.colors?.background || '#f5f5f5',
                                            borderBottom: `1px solid ${theme?.colors?.border || '#ddd'}`
                                        }}>
                                            <td style={{ padding: '0.75rem' }}>
                                                <strong>{formatDate(shift.date)}</strong>
                                            </td>
                                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                                                {shift.transactionCount}
                                            </td>
                                            <td style={{
                                                padding: '0.75rem',
                                                textAlign: 'right',
                                                fontWeight: 'bold',
                                                color: theme?.colors?.success || '#4caf50'
                                            }}>
                                                {formatCurrency(shift.totalTips)}
                                            </td>
                                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                                                {formatCurrency(shift.totalTips / shift.transactionCount)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
